import React from 'react';
import { FormSectionSimple } from 'components/layout/Section/FormSectionSimple';
import { useDynamicTranslation } from 'hooks/useDynamicTranslation';
import { NsKey } from 'enums/NsKey';

interface PricingData {
  initialPrice: number;
  newPrice: number;
  monthPrice: number;
  averageRate: number;
  totalPriceEightYears: number;
  discountPrice: number;
  discountPercentage: number;
}

interface Guarantee {
  id?: string;
  label: string;
  deductible?: number;
}

interface BorrowerPricingDetailsProps {
  pricing: PricingData;
  guarantees?: Guarantee[];
  showDiscount?: boolean;
  isGroupOffer?: boolean;
}

export const BorrowerPricingDetails: React.FC<BorrowerPricingDetailsProps> = ({
  pricing,
  guarantees,
  showDiscount = true,
  isGroupOffer = false
}) => {
  const { t } = useDynamicTranslation([NsKey.Common, NsKey.Pricing]);

  const formatPrice = (price: number) => {
    return new Intl.NumberFormat('fr-FR', {
      style: 'currency',
      currency: 'EUR',
      minimumFractionDigits: 2,
    }).format(price);
  };

  const hasDiscount = pricing.discountPrice > 0;

  return (
    <FormSectionSimple title={t('PRICING.LABELS.PRICING')}>
      <>
        {/* Prix principaux */}
        <div className="col-span-6 grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          {
            pricing.initialPrice !== undefined && pricing.initialPrice > 0 &&
            <div className="text-center p-4 bg-gradient-to-br from-gray-50 to-gray-100 rounded-xl border border-gray-200 relative overflow-hidden">
              {hasDiscount && (
                <div className="absolute top-2 right-2">
                  <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse"></div>
                </div>
              )}
              <span className="text-sm font-medium text-gray-500">{t('PRICING.LABELS.INITIAL_PRICE')}</span>
              <p className={`text-xl font-bold transition-all duration-300 ${hasDiscount
                ? 'text-gray-400 line-through decoration-2 decoration-red-500'
                : 'text-gray-900'
                }`}>
                {formatPrice(pricing.initialPrice)}
              </p>
            </div>
          }

          {/* Nouveau prix avec effet vert */}
          <div className={`text-center p-4 rounded-xl border transition-all duration-300 ${hasDiscount
            ? 'bg-gradient-to-br from-green-50 to-emerald-50 border-green-200 shadow-sm'
            : 'bg-white border-gray-200'
            }`}>
            <div className="flex items-center justify-center gap-2 mb-1">
              <span className={`text-sm font-medium ${hasDiscount ? 'text-green-700' : 'text-gray-500'
                }`}>
                {isGroupOffer ? t('PRICING.LABELS.NEW_PRICE') : t('PRICING.LABELS.TOTAL_COST')}
              </span>
              {hasDiscount && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold bg-green-100 text-green-800 animate-pulse">
                  -{pricing.discountPercentage.toFixed(0)}%
                </span>
              )}
            </div>
            <p className={`text-xl font-bold transition-all duration-300 ${hasDiscount
              ? 'text-green-600 scale-105'
              : 'text-gray-900'
              }`}>
              {formatPrice(pricing.newPrice)}
            </p>
            {hasDiscount && (
              <p className="text-xs text-green-600 font-medium mt-1">
                Économie: {formatPrice(pricing.discountPrice)}
              </p>
            )}
          </div>

          {/* Prix mensuel */}
          <div className="text-center p-4 bg-primary text-white rounded-xl relative overflow-hidden">
            <div className="absolute inset-0 bg-gradient-to-br from-transparent to-black/10"></div>
            <div className="relative z-10">
              <span className="text-sm font-medium opacity-90">{t('PRICING.LABELS.MONTH_PRICE')}</span>
              <p className="text-xl font-bold">
                {formatPrice(pricing.monthPrice)}
              </p>
            </div>
          </div>
        </div>

        {/* Informations complémentaires */}
        <div className="col-span-6 grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
            <span className="text-sm font-medium text-gray-500">{t('PRICING.LABELS.AVERAGE_RATE')}</span>
            <span className="text-sm font-semibold text-gray-900">
              {pricing.averageRate.toFixed(3)}%
            </span>
          </div>
          <div className="flex justify-between items-center p-3 bg-white rounded-lg border">
            <span className="text-sm font-medium text-gray-500">{t('PRICING.LABELS.TOTAL_PRICE_EIGHT_YEARS')}</span>
            <span className="text-sm font-semibold text-gray-900">
              {formatPrice(pricing.totalPriceEightYears)}
            </span>
          </div>
        </div>

        {/* Garanties */}
        {guarantees && guarantees.length > 0 && (
          <div className="col-span-6 mt-2">
            <div className="flex justify-between items-center mb-3">
              <h4 className="text-md font-medium text-gray-900">{t('PRICING.GUARANTEES.GUARANTEE')}</h4>
              {guarantees[0].deductible !== undefined && guarantees[0].deductible > 0 && (
                <span className="text-sm text-gray-600">
                  {t('PRICING.LABELS.DEDUCTIBLE')}: {guarantees[0].deductible} {guarantees[0].deductible > 1 ? 'jours' : 'jour'}
                </span>
              )}
            </div>
            <div className="space-y-2">
              {guarantees.map((guarantee, index) => (
                <div key={guarantee.id || index} className="flex justify-between items-center py-2 px-3 bg-white rounded-lg border hover:border-green-200 transition-colors">
                  <span className="text-sm text-gray-700">{guarantee.label}</span>
                  <span className="text-sm font-medium text-green-600 flex items-center gap-1">
                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                      <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                    </svg>
                    Inclus
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </>
    </FormSectionSimple>
  );
};
